import { useSyncExternalStore } from "react"
import { useTranslation } from "react-i18next"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { formatDateTime, formatRelative, toDate } from "@/lib/format"
import { cn } from "@/lib/utils"

// One shared 30s ticker for every <RelativeTime> on the page.
let now = Date.now()
const listeners = new Set<() => void>()
let timer: ReturnType<typeof setInterval> | undefined
function subscribe(cb: () => void) {
  listeners.add(cb)
  if (!timer)
    timer = setInterval(() => {
      now = Date.now()
      for (const l of listeners) l()
    }, 30_000)
  return () => {
    listeners.delete(cb)
    if (listeners.size === 0 && timer) {
      clearInterval(timer)
      timer = undefined
    }
  }
}
const getNow = () => now

/** "5 minutes ago" that stays fresh, with the absolute time in a tooltip. */
export function RelativeTime({
  value,
  fallback,
  className,
}: {
  value: string | number | Date | null | undefined
  fallback?: string
  className?: string
}) {
  const tick = useSyncExternalStore(subscribe, getNow, getNow)
  const { t, i18n } = useTranslation()
  const d = toDate(value)
  if (!d)
    return <span className={cn("text-muted-foreground", className)}>{fallback ?? t("common.never")}</span>
  // i18n.language participates so the label re-renders on language change
  const label = i18n.language ? formatRelative(d, tick) : ""
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <time dateTime={d.toISOString()} className={cn("whitespace-nowrap", className)}>
            {label}
          </time>
        }
      />
      <TooltipContent>{formatDateTime(d, "PPpp")}</TooltipContent>
    </Tooltip>
  )
}
